import { ICoreContext } from '@msdyn365-commerce/core';
import { Separator } from '../actions/get-localization.action';
import { LOCALIZATION_DEFAULT_NUMBER } from '../configuration/localization';
import { ILocalizationState } from '../states/localization-state';

const decimalScale = 3;
const allowLeadingZeros = false;

/**
 * Formats a number.
 * @param context - Core context.
 * @param date - Number to display.
 * @returns String value.
 */
// Example usage: formatNumber(this.props.context, new Date(CreatedDate)

export const formatNumber = (context: ICoreContext, number: number | undefined) => {
    if (number === undefined) {
        return '';
    }

    /**
     * don't do this, since it adds separators in unwanted places
    const numberOptions: INumberFormatOptions = { style: 'decimal' };
    const formattedNumber = context.cultureFormatter.formatNumber(number, numberOptions);
    //const formattedNumber = number.toString().replace(/\.\d+$/, '');
    */
    // Simply drop decimal points and convert to string
    // return Math.trunc(number).toString();
    return number.toString();
};

export const formatSliceNumber = (input: string) => {
    const dot = input.indexOf('.');
    if (dot === -1) {
        return input;
    }
    return input.slice(0, dot + 1 + decimalScale);
};

export const formatNumberLocaleStatic = (input: string | number, localizationState: ILocalizationState): string => {
    const decimalSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.decimalSeparator
        : LOCALIZATION_DEFAULT_NUMBER.decimalSeparator;
    const thousandSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.thousandSeparator
        : LOCALIZATION_DEFAULT_NUMBER.thousandSeparator;

    if (input === '0' || input === 0) {
        return '';
    }

    if (typeof input === 'number') {
        input = input.toString();
    }

    // Check if the decimal separator in the input matches the defined decimalSeparator
    if (decimalSeparator === Separator.Comma && input.includes(Separator.Dot)) {
        input = input.replace(Separator.Dot, Separator.Comma);
    } else if (decimalSeparator === Separator.Dot && input.includes(Separator.Comma)) {
        input = input.replace(Separator.Comma, Separator.Dot);
    }

    // Extract integer and decimal parts
    let [integerPart, decimalPart] = input.split(decimalSeparator);

    // Remove leading zeros unless it's a single zero
    if (!allowLeadingZeros) {
        integerPart = integerPart.replace(/^0+(?=\d)/, '');
    }

    // Apply thousand separator
    if (thousandSeparator && integerPart) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    }

    // Limit decimal places
    if (decimalPart) {
        decimalPart = decimalPart.slice(0, decimalScale).replace(/0+$/, '');
    }

    // Combine integer and decimal parts
    return decimalPart ? `${integerPart}${decimalSeparator}${decimalPart}` : integerPart;
};

export const formatNumberLocaleDynamic = (input: string | Number, localizationState: ILocalizationState): string => {
    const decimalSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.decimalSeparator
        : LOCALIZATION_DEFAULT_NUMBER.decimalSeparator;
    const thousandSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.thousandSeparator
        : LOCALIZATION_DEFAULT_NUMBER.thousandSeparator;

    if (typeof input === 'number') {
        input = input.toString();
    }

    // Allow only numbers and decimal separator
    let formattedValue = (input as string).replace(new RegExp(`[^0-9${decimalSeparator}]`, 'g'), '');

    // Prevent multiple decimal separators
    const parts = formattedValue.split(decimalSeparator);
    if (parts.length > 2) {
        formattedValue = parts[0] + decimalSeparator + parts.slice(1).join('');
    }

    // Extract integer and decimal parts
    let [integerPart, decimalPart] = formattedValue.split(decimalSeparator);

    // Remove leading zeros unless it's a single zero
    if (!allowLeadingZeros) {
        integerPart = integerPart.replace(/^0+(?=\d)/, '');
    }

    // Apply thousand separator (but not when user is typing decimal part)
    if (thousandSeparator && integerPart) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    }

    // Limit decimal places
    if (decimalPart) {
        decimalPart = decimalPart.slice(0, decimalScale);
    }

    // Combine integer and decimal parts
    return decimalPart !== undefined ? `${integerPart}${decimalSeparator}${decimalPart}` : integerPart;
};

export const removeTrailingZeros = (number: string) => {
    return parseFloat(number.replace(/,/g, '.'));
};

export const checkIfValidNumber = (number: string | number | undefined): boolean => {
    if (number && Number(number) > 0) {
        return true;
    } else {
        return false;
    }
};
